import {ColorSchemeType, Column, Flag, Label, Table, TableRow} from 'platform/components';
import {Box, Show, Text, ThemeColorPath, ThemeColorTextPath, VStack} from 'platform/foundation';
import {useDateTimeFormatter} from 'platform/locale';
import {match} from 'ts-pattern';

import {always} from 'ramda';

import {DefinitionWithTemplatesResponseBody} from '@dms/api/core';
import i18n from '@dms/i18n';
import {getLocaleLabel, useTenant} from '@dms/shared';

import {buildArray, EMPTY_PLACEHOLDER, parseDate} from 'shared/web';

import {useTemplateActions} from '../hooks/useTemplateActions';
import {DefinitionAuthorizationProfile} from '../types/Definition';

interface TemplateTableProps {
  definitionType: DefinitionWithTemplatesResponseBody['type'];
  authorizationProfile: DefinitionAuthorizationProfile | undefined;
  documentCode: string;
}

export function TemplatesTable(props: TemplateTableProps) {
  const formatDateTime = useDateTimeFormatter();
  const {isMultiLocale} = useTenant();

  const [getTemplateActions] = useTemplateActions(props.definitionType);

  const columns = buildArray<Column>()
    .add({width: '10%', element: <Label>{i18n.t('entity.document.labels.type')}</Label>})
    .add({width: '30%', element: <Label>{i18n.t('entity.document.labels.templateTitle')}</Label>})
    .when(isMultiLocale, {
      width: '10%',
      element: <Label>{i18n.t('general.labels.locales')}</Label>,
    })
    .add({width: '30%', element: <Label>{i18n.t('entity.document.labels.note')}</Label>})
    .add({width: '10%', element: <Label>{i18n.t('entity.document.labels.lastUpdate')}</Label>})
    .add({width: '10%', element: <Label>{i18n.t('entity.document.labels.addedBy')}</Label>});

  return (
    <VStack spacing={4}>
      <Table columns={columns}>
        {props.authorizationProfile?.templates.map((template) => {
          const isHidden = template.isHidden;
          const isPrimary = template.isPrimary;
          const updatedAt = template.updatedAt
            ? formatDateTime('dateShort', parseDate(template.updatedAt))
            : EMPTY_PLACEHOLDER;

          const color = match<[boolean, boolean], ThemeColorPath>([isPrimary, isHidden])
            .with([true, false], always('palettes.green.10.100'))
            .with([false, true], always('palettes.neutral.20.100'))
            .otherwise(always('general.transparent'));

          const hoverColor = match<[boolean, boolean], ThemeColorPath>([isPrimary, isHidden])
            .with([true, false], always('palettes.green.20.100'))
            .with([false, true], always('palettes.neutral.40.100'))
            .otherwise(always('palettes.neutral.20.100'));

          const flagColorScheme = match<[boolean, boolean], ColorSchemeType>([isPrimary, isHidden])
            .with([true, false], always('green'))
            .otherwise(always('neutral'));

          const flagLabel = match<[boolean, boolean], string>([isPrimary, isHidden])
            .with([true, false], always(i18n.t('entity.document.labels.primary')))
            .with([false, true], always(i18n.t('entity.document.labels.hidden')))
            .otherwise(always(i18n.t('entity.document.labels.alternative')));

          const textColor = match<boolean, ThemeColorTextPath>(isHidden)
            .with(true, always('tertiary'))
            .otherwise(always('primary'));

          return (
            <TableRow
              key={template.id}
              color={color}
              hoverColor={hoverColor}
              actions={{secondary: getTemplateActions(template)}}
              actionsWidth="1%"
            >
              <Box paddingVertical={2}>
                <Flag
                  colorScheme={flagColorScheme}
                  isSubtle={!isPrimary && !isHidden}
                  label={flagLabel}
                />
              </Box>

              <Text size="small" color={textColor}>
                {template.title}
              </Text>
              <Show when={isMultiLocale}>
                <Text size="small" color={textColor}>
                  {getLocaleLabel(template.locale) ?? EMPTY_PLACEHOLDER}
                </Text>
              </Show>
              <Text size="small" color={textColor}>
                {template.note || EMPTY_PLACEHOLDER}
              </Text>
              <Text size="small" color={textColor}>
                {updatedAt}
              </Text>
              <Text size="small" color={textColor}>
                {template.updatedBy?.fullName ?? EMPTY_PLACEHOLDER}
              </Text>
            </TableRow>
          );
        })}
      </Table>
    </VStack>
  );
}
